import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, AlertCircle, Loader2, LogOut } from 'lucide-react';
import dayjs from 'dayjs';
import { useAuth } from '../AuthContext';
import { toast } from 'sonner';

export default function CompleteProfilePage() {
  const navigate = useNavigate();
  const { user, completeProfile, logout } = useAuth();
  const [dob, setDob] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const isValid = dob && dayjs().diff(dayjs(dob), 'year') >= 13;

  const submit = async (e) => {
    e.preventDefault();
    if (!dob) { setErr('Please pick your date of birth'); return; }
    const age = dayjs().diff(dayjs(dob), 'year');
    if (age < 13) { setErr('You must be at least 13 years old'); return; }
    setErr(null);
    setSaving(true);
    try {
      await completeProfile(dob);
      toast.success('Profile complete!');
      navigate('/', { replace: true });
    } catch (e) {
      setErr(e?.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const bailOut = async () => {
    await logout();
    navigate('/welcome', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center pt-12 sm:pt-24 px-4">
      <div className="w-full max-w-md text-center" data-testid="complete-profile-page">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-2xl brand-gradient flex items-center justify-center">
            <Calendar className="w-7 h-7 text-white" />
          </div>
        </div>

        <h1 className="text-2xl font-black text-gray-900 tracking-tight">One quick step</h1>
        <p className="mt-2 text-sm text-gray-500 leading-relaxed px-4">
          Hi {user?.full_name?.split(' ')[0] || 'there'} — we need your date of birth to show
          age-appropriate events. This is required to continue.
        </p>

        <form onSubmit={submit} className="mt-8 text-left flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="complete-dob" className="text-xs font-bold text-gray-500">Date of birth</label>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                id="complete-dob"
                data-testid="complete-profile-dob-input"
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                max={dayjs().subtract(13, 'year').format('YYYY-MM-DD')}
                min={dayjs().subtract(100, 'year').format('YYYY-MM-DD')}
                className="w-full bg-white border-2 border-gray-200 rounded-xl pl-10 pr-4 py-4 text-[15px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          {err && (
            <div data-testid="complete-profile-error" className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm font-semibold">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {err}
            </div>
          )}

          <button
            data-testid="complete-profile-submit-btn"
            type="submit"
            disabled={saving || !isValid}
            className="w-full brand-gradient disabled:opacity-60 text-white py-4 rounded-2xl font-extrabold text-base transition-opacity mt-2"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Continue'}
          </button>

          <button
            type="button"
            data-testid="complete-profile-logout-btn"
            onClick={bailOut}
            className="flex items-center justify-center gap-1.5 py-3 text-gray-400 text-sm font-bold hover:text-gray-600 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out instead
          </button>
        </form>
      </div>
    </div>
  );
}
